import { useState, useEffect, useCallback, useRef } from 'react';

import API from '@scale/frontend-lib/dashboard-api';
import apiFetch from '@scale/frontend-lib/api-fetch';
import {
  ChatApiEndpointFallbackStrategy,
  ChatApiEndpoint,
  ChatApiEndpointWithId,
  ChatBenchmarkConfig,
  ProvidedModelResponse,
  ChatAttachment,
  MessageRole,
  CreateCompletionPair,
  CandidateMessageWithMetadata,
} from '@scale/shared/types/chat';
import {
  CreateCompletionRequest,
  CreateCompletionResponse,
  StrategyName,
  Message,
  CandidateState,
  UserMessage,
  CandidateMessage,
  getStrategyFromUrl,
  doesStrategySupportAttachments,
  StrategiesThatSupportAttachments,
  ModelRequestUseCase,
  ToolUseOptions,
  MessageMetadata,
} from '@scale/shared/types/chat-completion';
import logger from '@scale/frontend-lib/logging';
import _ from 'lodash';
import produce from 'immer';
import HTTPError from '@scale/frontend-lib/httpError';
import useUnleashFeatureFlag from 'react-scale/hooks/useUnleashFeatureFlag';
import { useMostRecentResponseForStepId } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { useProjectModelConfiguration } from 'react-scale/hooks/chat/useProjectModelConfigs';
import { PopulatedProjectModelConfigDTO } from '@scale/customer-app-shared';

const INITIAL_MODEL_RESPONSE_RETRY_COOLDOWN_SECS = 5;
const MAX_MODEL_RESPONSE_RETRY_COOLDOWN_SECS = 80;

interface UseChatCompletionParams {
  chatApiEndpoints: (ChatApiEndpoint | ChatApiEndpointWithId)[];
  providedModelResponses: ProvidedModelResponse[];
  projectId: string | null;
  useCase: ModelRequestUseCase;
  options?: {
    chatBenchmarkConfig?: ChatBenchmarkConfig;
    toolUseOptions?: ToolUseOptions;
    omitAttachments?: boolean;
    enableSelectionWithErrors?: boolean;
    maxModelResponseErrorsForSelection?: number;
    templateVariables?: Record<string, string>;
    projectModelConfigurationMapping?: Record<string, PopulatedProjectModelConfigDTO>;
    isTaskPreview?: boolean;
  };
  metadata?: any;
  pushInteractionHistoryOnTurn?: (pair: CreateCompletionPair) => void;
  stepId?: string;
  responseIndex?: number;
  setProjectModelConfigurationMapping?: (
    projectModelConfigurationMapping: Record<string, PopulatedProjectModelConfigDTO>,
  ) => void;
}

const useChatCompletion = ({
  chatApiEndpoints: chatApiEndpoints_,
  providedModelResponses,
  projectId,
  useCase,
  options,
  metadata,
  pushInteractionHistoryOnTurn,
  stepId,
  responseIndex,
  setProjectModelConfigurationMapping,
}: UseChatCompletionParams) => {
  const enableFallbackEndpoint = useUnleashFeatureFlag(
    'fallback_model_endpoint',
    { projectId: projectId ?? undefined },
    true,
  );
  const treatNullAsEmptyResponse = useUnleashFeatureFlag(
    'chat-model-response-treat-null-as-empty-response',
    { projectId: projectId ?? undefined },
    false,
  );

  const chatApiEndpointsRef = useRef<{
    chatApiEndpoints: ChatApiEndpoint[];
    projectModelConfigsLoaded: boolean;
  }>({
    chatApiEndpoints: [],
    projectModelConfigsLoaded: false,
  });

  const { populatedMapping, projectModelConfigsLoadedRef } = useProjectModelConfiguration({
    templateVariables: options?.templateVariables,
    projectModelConfigurationMapping: options?.projectModelConfigurationMapping,
    setProjectModelConfigurationMapping,
    projectId,
    isTaskPreview: options?.isTaskPreview,
  });

  useEffect(() => {
    // Use Ref to make sure fetchCandidates uses populated model versions when using project model configs
    chatApiEndpointsRef.current = getChatEndpoints(
      chatApiEndpoints_,
      options?.chatBenchmarkConfig,
      populatedMapping,
      projectModelConfigsLoadedRef.current,
    );
  }, [
    projectModelConfigsLoadedRef,
    chatApiEndpoints_,
    options?.chatBenchmarkConfig,
    populatedMapping,
  ]);

  const [candidates, setCandidates] = useState<CandidateMessage[]>([]);
  const [refetchCandidateCooldown, setRefetchCandidateCooldown] = useState<number>(0);
  const [numRetries, setNumRetries] = useState<number>(0);
  const [endpointIdToNumRetries, setEndpointIdToNumRetries] = useState<Record<string, number>>({});
  const [modelResponseRetryDelaySecs, setModelResponseRetryDelaySecs] = useState<number>(
    INITIAL_MODEL_RESPONSE_RETRY_COOLDOWN_SECS,
  );
  const configLoadRetriesRef = useRef<number>(0);
  const BASE_RETRY_DELAY = 100;

  const lastResponse = useMostRecentResponseForStepId(stepId, responseIndex);
  const getEndpointFromLastResponse = (candidateIndex: number) => {
    return lastResponse?.context?.candidates?.[candidateIndex]?.endpoint;
  };

  const handleSetRefetchCandidateCooldown = () => {
    setRefetchCandidateCooldown(modelResponseRetryDelaySecs);
    setModelResponseRetryDelaySecs(
      Math.min(modelResponseRetryDelaySecs * 2, MAX_MODEL_RESPONSE_RETRY_COOLDOWN_SECS),
    );
    setNumRetries(prev => prev + 1);
  };

  const getEndpointOrFallback = (
    candidateIndex: number,
  ): { endpoint: ChatApiEndpoint | ChatApiEndpointWithId | undefined; isFallback: boolean } => {
    const endpoint = chatApiEndpointsRef.current.chatApiEndpoints[candidateIndex];
    const endpointId = (endpoint as ChatApiEndpointWithId).id;

    if (!enableFallbackEndpoint) {
      return { endpoint, isFallback: false };
    }

    // The default behavior is that we should call the same model across turns. If there are prior turns, only use
    // the fallback if previous responses did as well.
    const maybeLastResponseEndpoint = getEndpointFromLastResponse(candidateIndex);
    if (maybeLastResponseEndpoint) {
      const lastResponseIsFallback = maybeLastResponseEndpoint?.id !== endpointId;
      return { endpoint: maybeLastResponseEndpoint, isFallback: lastResponseIsFallback };
    }

    if (
      endpoint.fallbackStrategy === ChatApiEndpointFallbackStrategy.FALLBACK_ON_ERROR &&
      !_.isNil(endpoint.fallbackEndpoint) &&
      !_.isNil(endpointId) &&
      !_.isNil(endpointIdToNumRetries[endpointId]) &&
      endpointIdToNumRetries[endpointId] > 0
    ) {
      return { endpoint: endpoint.fallbackEndpoint, isFallback: true };
    }
    return { endpoint, isFallback: false };
  };

  const setCandidate = useCallback(
    (
      candidateIndex: number,
      update: {
        state?: CandidateState;
        content?: string;
        isCorrupted?: boolean;
        additionalData?: Record<string, any>;
        attachments?: Array<ChatAttachment>;
        errorMessage?: string;
        messageMetadata?: MessageMetadata;
        endpoint?: ChatApiEndpointWithId;
      },
    ) => {
      setCandidates(prev =>
        produce(prev, draft => {
          if (!draft[candidateIndex]) {
            return;
          }
          if (update.state !== undefined) {
            draft[candidateIndex].state = update.state;
          }
          if (update.content !== undefined) {
            draft[candidateIndex].message.content = update.content;
          }
          if (update.isCorrupted !== undefined) {
            draft[candidateIndex].isCorrupted = update.isCorrupted;
          }
          if (update.additionalData !== undefined) {
            draft[candidateIndex].additionalData = update.additionalData;
          }
          if (update.attachments !== undefined) {
            draft[candidateIndex].attachments = update.attachments;
          }
          if (update.errorMessage !== undefined) {
            draft[candidateIndex].errorMessage = update.errorMessage;
          }
          if (update.messageMetadata !== undefined) {
            draft[candidateIndex].message.messageMetadata = update.messageMetadata;
          }
          if (update.endpoint !== undefined) {
            (draft[candidateIndex] as CandidateMessageWithMetadata).endpoint = update.endpoint;
          }
        }),
      );
    },
    [],
  );

  useEffect(() => {
    const interval = setInterval(() => {
      if (refetchCandidateCooldown > 0) {
        setRefetchCandidateCooldown(prev => prev - 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [refetchCandidateCooldown]);

  const canRefresh = candidates.every(candidate => candidate.state !== CandidateState.Loading);
  let areCandidatesFullyLoaded;
  if (options?.enableSelectionWithErrors) {
    areCandidatesFullyLoaded =
      candidates.every(
        candidate =>
          candidate.state === CandidateState.Completed || candidate.state === CandidateState.Error,
      ) &&
      candidates.filter(candidate => candidate.state === CandidateState.Completed).length >= 1 &&
      candidates.filter(candidate => candidate.state === CandidateState.Error).length <=
        (options?.maxModelResponseErrorsForSelection ?? 1);
  } else {
    areCandidatesFullyLoaded = candidates.every(
      candidate => candidate.state === CandidateState.Completed,
    );
  }
  const hasErrorWhenFetchingCandidates = candidates.some(
    candidate => candidate.state === CandidateState.Error,
  );

  const fetchCandidate = useCallback(
    // eslint-disable-next-line complexity
    async (
      candidateIndex: number,
      userMessage_: Message,
      historyMessages: Message[],
      additionalEndpointParams?: any,
    ) => {
      const shouldCorruptMessage = (() => {
        if (!options?.chatBenchmarkConfig?.modelResponseCorruptionConfig) {
          return false;
        }
        if (providedModelResponses.length > 0) {
          return true;
        } else {
          return candidateIndex !== 0;
        }
      })();

      const startTime = Math.floor(Date.now());
      setCandidate(candidateIndex, {
        content: '',
        state: CandidateState.Loading,
        isCorrupted: shouldCorruptMessage ? true : undefined,
      });

      const userMessage = _.cloneDeep(userMessage_);

      let messages = getMessages(
        userMessage,
        historyMessages,
        !!shouldCorruptMessage,
        options?.chatBenchmarkConfig,
      );

      // remove any messages with skipMessage set to true
      messages = messages.filter(message => !message.skipMessage);

      const { endpoint, isFallback } = getEndpointOrFallback(candidateIndex);
      if (!endpoint) {
        return;
      }
      const { url, params } = endpoint;
      const endpointId = (endpoint as ChatApiEndpointWithId)?.id;

      if (isFallback) {
        // Fallback endpoint always has an ID
        setCandidate(candidateIndex, { endpoint: endpoint as ChatApiEndpointWithId });
      }

      const strategyName: StrategyName | undefined = getStrategyFromUrl(url);
      // if any of the messages have attachments, check if the strategy supports attachments
      const hasAttachments = messages.some(
        message => message.attachments && message.attachments.length > 0,
      );
      if (
        hasAttachments &&
        (options?.omitAttachments || !StrategiesThatSupportAttachments.includes(strategyName!))
      ) {
        // remove attachments from messages if strategy does not support attachments
        messages.forEach(message => {
          message.attachments = undefined;
        });
      }

      if (!strategyName) {
        try {
          const response: any = await API.selfserve.fetchResponsesFromChatEndpoint({
            endpointUrl: url ?? '',
            getCompletionsRequest: {
              numCompletions: 1,
              nextPrompt: userMessage.content,
              history: { interactions: [] },
              additionalParams: params,
            },
            taskCustomer: undefined,
          });
          setCandidate(candidateIndex, {
            state: CandidateState.Completed,
            content: response.completions[0].text,
          });
          setModelResponseRetryDelaySecs(INITIAL_MODEL_RESPONSE_RETRY_COOLDOWN_SECS);
        } catch (_e) {
          setCandidate(candidateIndex, {
            state: CandidateState.Error,
          });
          logger.error('[useChatCompletion] Error fetching candidate: ', _e);
          handleSetRefetchCandidateCooldown();
        }
        return;
      }

      const createCompletionParams = { ...params, ...additionalEndpointParams };
      const body: CreateCompletionRequest = {
        strategyName,
        url,
        params: createCompletionParams,
        messages,
        metadata,
        projectId,
        useCase,
        toolUseOptions: options?.toolUseOptions,
      };

      if (params.stream) {
        let responseText = '';
        let messageMetadata: MessageMetadata | undefined = undefined;

        try {
          await apiFetch(null, 'internal/self_serve/rlhf/createCompletion', {
            method: 'POST',
            body,
            fetchEventSourceOptions: {
              openWhenHidden: true,
              onopen: async response => {
                if (!response.ok) {
                  throw new Error(
                    'Failed to get model response. Please try again. Response: ' +
                      JSON.stringify(response || {}),
                  );
                }
              },
              onmessage: event => {
                if (event.data === '[DONE]' || event.data === '[IGNORE]') {
                  return;
                }
                const parsed = JSON.parse(event.data);
                if (parsed.error) {
                  throw new Error(parsed.error);
                }
                if (parsed.fullOutput) {
                  responseText = parsed.fullOutput;
                } else if (parsed.output) {
                  responseText += parsed.output;
                } else if (parsed.choices?.[0]?.delta?.content) {
                  responseText += parsed.choices[0].delta.content as string;
                }

                if (parsed.messageMetadata) {
                  messageMetadata = parsed.messageMetadata;
                }

                setCandidate(candidateIndex, {
                  content: responseText,
                  messageMetadata,
                });
              },
              onclose: () => {
                setCandidate(candidateIndex, {
                  content: responseText,
                  messageMetadata,
                  state: CandidateState.Completed,
                });
                const timestamp = Date.now();
                pushInteractionHistoryOnTurn?.({
                  request: body,
                  response: {
                    content: responseText,
                    messageMetadata,
                    latencyInMs: timestamp - startTime,
                  },
                  context: {
                    timestamp,
                    endpointId,
                  },
                });
                setModelResponseRetryDelaySecs(INITIAL_MODEL_RESPONSE_RETRY_COOLDOWN_SECS);
              },
              onerror: e => {
                throw e;
              },
            },
          });
        } catch (e) {
          setCandidate(candidateIndex, {
            state: CandidateState.Error,
          });
          const timestamp = Date.now();
          pushInteractionHistoryOnTurn?.({
            request: body,
            response: { error: e, latencyInMs: timestamp - startTime },
            context: {
              timestamp,
              endpointId,
            },
          });
          logger.error('[useChatCompletion] Error fetching candidate: ', e);
          if (!_.isNil(endpointId)) {
            setEndpointIdToNumRetries(prevEndpointIdToNumRetries => ({
              ...prevEndpointIdToNumRetries,
              [endpointId]: (prevEndpointIdToNumRetries[endpointId] || 0) + 1,
            }));
          }
          handleSetRefetchCandidateCooldown();
        }
      } else {
        try {
          const completionUrl = 'internal/self_serve/rlhf/createCompletion';

          const response = await apiFetch<CreateCompletionResponse>(null, completionUrl, {
            method: 'POST',
            body,
          });

          if (response.error) {
            throw new Error(response.error);
          }

          let attachments = response.choices[0].attachments;
          if (!(attachments && attachments.length > 0)) {
            attachments = undefined;
          }

          const messageMetadata = response.choices[0].messageMetadata ?? undefined;
          const responseContentRaw = response.choices[0].content;
          const responseContent = treatNullAsEmptyResponse
            ? responseContentRaw ?? ''
            : responseContentRaw;
          // If the response content is nil, then this indicates a model error
          setCandidate(candidateIndex, {
            state: !_.isNil(responseContent) ? CandidateState.Completed : CandidateState.Error,
            content: responseContent ?? '',
            additionalData: response.additionalData,
            attachments,
            messageMetadata,
          });
          const timestamp = Date.now();
          pushInteractionHistoryOnTurn?.({
            request: body,
            response: {
              content: responseContent,
              messageMetadata,
              latencyInMs: timestamp - startTime,
            },
            context: {
              timestamp,
              endpointId,
            },
          });
          setModelResponseRetryDelaySecs(INITIAL_MODEL_RESPONSE_RETRY_COOLDOWN_SECS);
        } catch (_e) {
          let errorMessage = _e.message;
          if (_e instanceof HTTPError) {
            // Use error message if it includes 'Error Id' set on backend - this is to prevent unintentionally leaking internal messages
            const includesErrorId =
              typeof _e.response?.message === 'string' &&
              _e.response.message.toLowerCase().includes('error id');
            errorMessage = includesErrorId ? _e.response?.message : _e.message;
          }
          setCandidate(candidateIndex, {
            state: CandidateState.Error,
            errorMessage,
          });
          const timestamp = Date.now();
          pushInteractionHistoryOnTurn?.({
            request: body,
            response: { error: _e, latencyInMs: timestamp - startTime },
            context: {
              timestamp,
              endpointId,
            },
          });
          logger.error('[useChatCompletion] Error fetching candidate: ', _e);
          handleSetRefetchCandidateCooldown();
          if (!_.isNil(endpointId)) {
            setEndpointIdToNumRetries(prevEndpointIdToNumRetries => ({
              ...prevEndpointIdToNumRetries,
              [endpointId]: (prevEndpointIdToNumRetries[endpointId] || 0) + 1,
            }));
          }
        }
      }
    },
    [
      lastResponse,
      chatApiEndpointsRef.current.chatApiEndpoints,
      setCandidate,
      endpointIdToNumRetries,
      options,
      providedModelResponses.length,
      treatNullAsEmptyResponse,
    ],
  );

  const fetchCandidates = useCallback(
    async (
      userMessage: Message,
      historyMessages: Message[],
      systemPromptGptOnly?: boolean,
      additionalEndpointParams?: any,
      skipEarlyReturn?: boolean,
    ) => {
      if (
        !skipEarlyReturn &&
        (!canRefresh ||
          (historyMessages.length > 0 &&
            historyMessages[historyMessages.length - 1].role === MessageRole.User))
      ) {
        return;
      }

      // If project model configurations aren't loaded yet, retry after a delay
      // Skip this if we have provided model responses
      if (
        configLoadRetriesRef.current <= 10 &&
        !providedModelResponses.length &&
        (!chatApiEndpointsRef.current.projectModelConfigsLoaded ||
          !chatApiEndpointsRef.current.chatApiEndpoints.length)
      ) {
        configLoadRetriesRef.current++;
        setTimeout(() => {
          fetchCandidates(
            userMessage,
            historyMessages,
            systemPromptGptOnly,
            additionalEndpointParams,
            skipEarlyReturn,
          );
        }, BASE_RETRY_DELAY * (configLoadRetriesRef.current + 1));
        return;
      }

      setCandidates(
        _.range(
          chatApiEndpointsRef.current.chatApiEndpoints.length + providedModelResponses.length,
        ).map(i => ({
          index: i,
          state: CandidateState.Loading,
          message: {
            role: MessageRole.Assistant,
            content: '',
          },
          sourceId:
            i < chatApiEndpointsRef.current.chatApiEndpoints.length
              ? (chatApiEndpointsRef.current.chatApiEndpoints[i] as ChatApiEndpointWithId)?.id
              : providedModelResponses[i - chatApiEndpointsRef.current.chatApiEndpoints.length].id,
        })),
      );
      providedModelResponses.forEach((response, i) => {
        setCandidate(i + chatApiEndpointsRef.current.chatApiEndpoints.length, {
          state: CandidateState.Completed,
          content: response.content,
          attachments: response?.attachments,
        });
      });
      const systemFilteredHistoryMessages = historyMessages.filter(
        m => m.role !== MessageRole.System,
      );
      await Promise.all(
        chatApiEndpointsRef.current.chatApiEndpoints.map((endpoint, i) =>
          fetchCandidate(
            i,
            userMessage,
            systemPromptGptOnly &&
              !((endpoint as ChatApiEndpointWithId).id ?? endpoint.name ?? '').includes('gpt')
              ? systemFilteredHistoryMessages
              : historyMessages,
            additionalEndpointParams,
          ),
        ),
      );
    },
    [canRefresh, providedModelResponses, setCandidate, fetchCandidate, options?.isTaskPreview],
  );

  const fetchCandidatesMultiMessage = useCallback(
    async (
      userMessages: UserMessage[],
      historyMessages: Message[],
      systemPromptGptOnly?: boolean,
      additionalEndpointParams?: any,
    ) => {
      if (
        !canRefresh ||
        (historyMessages.length > 0 &&
          historyMessages[historyMessages.length - 1].role === MessageRole.User)
      ) {
        return;
      }
      const minLength = Math.min(
        userMessages.length,
        chatApiEndpointsRef.current.chatApiEndpoints.length,
      );
      setCandidates(
        _.range(minLength + providedModelResponses.length).map(i => ({
          index: i,
          state: CandidateState.Loading,
          message: {
            role: MessageRole.Assistant,
            content: '',
          },
          attachments: [],
          sourceId:
            i < minLength
              ? (chatApiEndpointsRef.current.chatApiEndpoints[i] as ChatApiEndpointWithId)?.id
              : providedModelResponses[i - minLength].id,
        })),
      );
      providedModelResponses.forEach((response, i) => {
        setCandidate(i + minLength, {
          state: CandidateState.Completed,
          content: response.content,
        });
      });
      const systemFilteredHistoryMessages = historyMessages.filter(
        m => m.role !== MessageRole.System,
      );
      await Promise.all(
        chatApiEndpointsRef.current.chatApiEndpoints.map((endpoint, i) => {
          if (i < minLength) {
            fetchCandidate(
              i,
              userMessages[i],
              systemPromptGptOnly &&
                !((endpoint as ChatApiEndpointWithId).id ?? endpoint.name ?? '').includes('gpt')
                ? systemFilteredHistoryMessages
                : historyMessages,
              additionalEndpointParams,
            );
          }
        }) ?? [],
      );
    },
    [
      canRefresh,
      chatApiEndpointsRef.current.chatApiEndpoints,
      providedModelResponses,
      setCandidate,
      fetchCandidate,
    ],
  );

  const clearCandidates = useCallback(() => {
    setCandidates([]);
  }, [setCandidates]);

  return {
    candidates,
    setCandidates,
    fetchCandidate,
    fetchCandidates,
    fetchCandidatesMultiMessage,
    clearCandidates,
    refetchCandidateCooldown,
    numRetries,

    areCandidatesFullyLoaded,
    hasErrorWhenFetchingCandidates,
  };
};

// replaces the project model endpoints with the project model configuration mapping if it exists otherwise returns the original endpoints
const populateProjectModelEndpoints = (
  chatEndpoints: (ChatApiEndpoint | ChatApiEndpointWithId)[],
  projectModelConfigurationMapping?: Record<string, PopulatedProjectModelConfigDTO>,
) => {
  const projectModelEndpoints: ChatApiEndpoint[] = [];

  for (const endpoint of chatEndpoints) {
    const key = endpoint.projectModelConfigurationKey;
    // cache configurations using interaction history so we keep same configuration throughout task
    if (key && projectModelConfigurationMapping && projectModelConfigurationMapping[key]) {
      const projectEndpoint = {
        ...endpoint,
        ...projectModelConfigurationMapping[key],
        ...(projectModelConfigurationMapping[key].fallbackEndpoint && {
          fallbackEndpoint: {
            ...projectModelConfigurationMapping[key].fallbackEndpoint,
            ...((endpoint as ChatApiEndpointWithId).id && {
              id: `${(endpoint as ChatApiEndpointWithId).id}_fallback`,
            }),
          },
        }),
      };
      projectModelEndpoints.push(projectEndpoint as ChatApiEndpoint);
      continue;
    }
    projectModelEndpoints.push(endpoint);
  }
  return projectModelEndpoints;
};

function getChatEndpoints(
  origChatApiEndpoints: (ChatApiEndpoint | ChatApiEndpointWithId)[],
  chatBenchmarkConfig?: ChatBenchmarkConfig,
  projectModelConfigurationMapping?: Record<string, PopulatedProjectModelConfigDTO>,
  projectModelConfigsLoaded?: boolean,
): {
  chatApiEndpoints: ChatApiEndpoint[];
  projectModelConfigsLoaded: boolean;
} {
  const modelResponseCorruptionConfig = chatBenchmarkConfig?.modelResponseCorruptionConfig;

  const endpoints = populateProjectModelEndpoints(
    origChatApiEndpoints,
    projectModelConfigurationMapping,
  );

  if (!modelResponseCorruptionConfig)
    return {
      chatApiEndpoints: endpoints,
      projectModelConfigsLoaded: projectModelConfigsLoaded ?? false,
    };

  const endpoint = modelResponseCorruptionConfig.overridenChatApiEndpoint ?? {
    url: 'https://api.openai.com/v1/chat/completions',
    params: {
      model: 'gpt-3.5-turbo',
      max_tokens: 1024,
      temperature: 0,
      stream: true,
    },
  };

  const timesEndpoints = _.times(origChatApiEndpoints.length, () => endpoint);

  return {
    chatApiEndpoints: timesEndpoints,
    projectModelConfigsLoaded: projectModelConfigsLoaded ?? false,
  };
}

function getMessages(
  userMessage_: Message,
  historyMessages: Message[],
  shouldCorrupt: boolean,
  chatBenchmarkConfig?: ChatBenchmarkConfig,
): Message[] {
  const userMessage = _.cloneDeep(userMessage_);

  const messages = [...historyMessages, userMessage];

  if (shouldCorrupt && chatBenchmarkConfig?.modelResponseCorruptionConfig) {
    messages.unshift({
      role: MessageRole.System,
      content: chatBenchmarkConfig.modelResponseCorruptionConfig.systemMessage,
    });
  }

  return messages;
}

// TODO(code-cleanup): Implement the streaming functionality and then move this
// function back into the hook to avoid duplication.
export async function fetchCandidateForEndpoint(
  chatApiEndpoints: ChatApiEndpoint[],
  candidateIndex: number,
  userMessage_: UserMessage,
  historyMessages: Message[],
  shouldCorruptMessage: boolean,
  useCase: ModelRequestUseCase,
  chatBenchmarkConfig?: ChatBenchmarkConfig,
  queryCache?: { [key: string]: string },
) {
  const userMessage = _.cloneDeep(userMessage_);
  const messages = getMessages(
    userMessage,
    historyMessages,
    !!shouldCorruptMessage,
    chatBenchmarkConfig,
  );

  const endpoint = chatApiEndpoints[candidateIndex];
  const { url, params } = endpoint;

  const strategyName: StrategyName | undefined = getStrategyFromUrl(url);
  if (!strategyName || !doesStrategySupportAttachments(strategyName)) {
    messages.forEach(message => {
      message.attachments = undefined;
    });
  }
  if (!strategyName) {
    const response: any = await API.selfserve.fetchResponsesFromChatEndpoint({
      endpointUrl: url,
      getCompletionsRequest: {
        numCompletions: 1,
        nextPrompt: userMessage.content,
        history: { interactions: [] },
        additionalParams: params,
      },
      taskCustomer: undefined,
    });
    return response.completions[0].text;
  }

  const body: CreateCompletionRequest = {
    strategyName,
    url,
    params,
    messages,
    projectId: params.projectId || null,
    useCase,
  };

  // Remove stream parameter since we are not using this result in the UI
  if ((body.params as any).stream) {
    body.params = _.omit(body.params, 'stream');
  }

  const queryCacheKey = JSON.stringify(body);
  if (queryCache && queryCache[queryCacheKey]) {
    return queryCache[queryCacheKey];
  }
  const response = await apiFetch<CreateCompletionResponse>(
    null,
    'internal/self_serve/rlhf/createCompletion',
    {
      method: 'POST',
      body,
    },
  );
  const content = response.choices[0].content;
  if (queryCache) {
    queryCache[queryCacheKey] = content;
  }
  return content;
}

export default useChatCompletion;
